import { Injectable } from '@angular/core';
import { LocationApiService } from '../location-api/location-api.service';
import { VehiclesManagerService } from '../vehicles/vehicles-manager.service';
import { ToastService } from '../../shared/services/toast.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FeatureCollection, GeoJSON, Point } from 'geojson';
import { BackendLocationModel, TrackViewState } from '../../shared/models/location.model';
import { VehicleLocationUpdate } from '../../shared/models/vehicle-breadcrumb';

@Injectable({
  providedIn: 'root',
})
export class LocationTrackingPollerService {

  readonly viewStateInterval = 30; // seconds
  readonly trackHeadInterval = 5; // seconds

  private isInitialized = false;
  private readonly openSubscriptions = Array<Subscription>();

  readonly currentLocationsObservable = new BehaviorSubject<GeoJSON>(JSON.parse('{"type": "FeatureCollection", "features": []}'));
  readonly historyLocationsObservable = new BehaviorSubject<GeoJSON>(JSON.parse('{"type": "FeatureCollection", "features": []}'));
  readonly tileUrlObservable = new BehaviorSubject<string>(null);
  readonly viewStateObservable = new BehaviorSubject<TrackViewState>(null);

  currentSnapshot: number = null;
  currentViewState: TrackViewState = null;
  currentLocationGeojson: FeatureCollection = null;

  trackViewStateIntervalId: NodeJS.Timeout;
  trackHeadIntervalId: NodeJS.Timeout;

  constructor(private readonly locationApi: LocationApiService,
              private readonly vehiclesManager: VehiclesManagerService,
              private readonly toast: ToastService,
  ) { }

  public init() {
    if (this.isInitialized) {
      throw Error('The LocationTrackingPoller has already been initialized.');
    }
    this.connectToManager();

    this.isInitialized = true;
  }

  public release() {
    if (!this.isInitialized) {
      return;
    }

    if (!!this.trackViewStateIntervalId) {
      clearInterval(this.trackViewStateIntervalId);
    }

    if (!!this.trackHeadIntervalId) {
      clearInterval(this.trackHeadIntervalId);
    }

    for (const subscription of this.openSubscriptions) {
      subscription.unsubscribe();
    }
    this.openSubscriptions.length = 0;
    this.isInitialized = false;
  }

  private connectToManager() {
    // initial execution
    this.updateTrackViewState();

    // delayed and regular executions
    this.trackViewStateIntervalId = setInterval(() => {
      this.updateTrackViewState();
    }, this.viewStateInterval * 1000);

    this.trackHeadIntervalId = setInterval(() => {
      // delayed and regular executions
      this.updateTrackHeadsAndPoints();
    }, this.trackHeadInterval * 1000);
  }

  private updateTrackViewState() {
    this.locationApi.getTrackViewState().then(trackViewState => {
      if (this.currentSnapshot !== trackViewState.snapshot) {
        this.currentSnapshot = trackViewState.snapshot;
        this.currentViewState = trackViewState;
        this.viewStateObservable.next(this.currentViewState);
        this.tileUrlObservable.next(this.locationApi.getLocationHistoryVectorTileUrl(trackViewState.snapshot));
        this.updateTrackHeadsAndPoints();
      }
    }).catch(error => {
      console.error(error);
      this.toast.long(`Location Tracking API returned error. Re-trying in ${this.viewStateInterval} seconds...`);
    });
  }

  private updateTrackHeadsAndPoints() {
    if (this.currentSnapshot !== null) {
      Promise.all([
        this.locationApi.getLocationHistoryTrackHeads(this.currentSnapshot),
        this.getLatestCurrentLocationGeojson(),
      ]).then(
        responses => {
          const [trackHeadResponse, currentPoints] = responses;
          const trackHeadFeatures = (trackHeadResponse as FeatureCollection)
            .features
            .filter(f => f.geometry.type === 'LineString');

          // update track head lines
          this.historyLocationsObservable.next({
            type: 'FeatureCollection',
            features: trackHeadFeatures
          });

          // prepare point feature collection
          const vehicleIdToShiftIdMap = new Map<number, number>();
          const pointFeaturesFromHeadResponse = (trackHeadResponse as FeatureCollection)
            .features
            .filter(f => f.geometry.type === 'Point');
          const pointFeatures = (currentPoints as FeatureCollection).features;
          pointFeaturesFromHeadResponse.forEach((point) => {
            const shiftId = vehicleIdToShiftIdMap.get(point.properties.locationsourceid);
            if (shiftId) {
              if (point.properties.sessionkey < shiftId) {
                // ignore this point as it is from previous shift
                return;
              }
            } else {
              vehicleIdToShiftIdMap.set(point.properties.locationsourceid, point.properties.sessionkey);
            }
            const existingPointIndex = pointFeatures
              .findIndex(f => {
                return f.properties.locationsourceid === point.properties.locationsourceid;
              });
            if (existingPointIndex > -1) {
              // replace point geometry from track/head source
              pointFeatures[existingPointIndex].geometry = point.geometry;
              // replace point heading from track/head source
              pointFeatures[existingPointIndex].properties.heading = point.properties.heading;
              pointFeatures[existingPointIndex].properties.flags = point.properties.flags;
              pointFeatures[existingPointIndex].properties.unixtime = point.properties.unixtime;
              if (pointFeatures[existingPointIndex].properties.sessionkey !== point.properties.sessionkey) {
                // new shift started for the vehicle
                pointFeatures[existingPointIndex].properties.sessionkey = point.properties.sessionkey;
              }
            } else {
              // current location point for vehicleId not found, new shift was started after current locations load and it is
              // the first vehicle shift
              pointFeatures.push(point);
            }
          });

          // update observable - map markers
          this.currentLocationGeojson = {
            type: 'FeatureCollection',
            features: pointFeatures
          };
          this.currentLocationsObservable.next(this.currentLocationGeojson);

          // update location in vehicle manager - used to display in Asset detail
          const vehicleLocations = pointFeatures.map(feature => {
            const prop = feature.properties;
            const coord = (feature.geometry as Point).coordinates;
            return new VehicleLocationUpdate(
              feature.properties.locationsourceid,
              new Date(prop.unixtime * 1000),
              {
                id: prop.id,
                longitude: coord[0],
                latitude: coord[1],
                speed: prop.speed,
                heading: prop.heading,
                flags: prop.flags,
                gpsSource: prop.gpssource,
              } as BackendLocationModel
            );
          });
          this.vehiclesManager.updateVehicleLocation(vehicleLocations);
        }
      ).catch(error => {
        console.warn('Getting track heads failed!');
      });
    } else {
      console.warn('Current snapshot is null!');
    }
  }

  private getLatestCurrentLocationGeojson(): Promise<FeatureCollection> {
    if (this.currentLocationGeojson) {
      return new Promise((resolve, reject) => {
        resolve(this.currentLocationGeojson);
      });
    } else {
      return this.locationApi.getCurrentLocationPoints();
    }
  }
}
